// src/components/terms/TermsAndConditions.jsx


// src/components/terms/TermsAndConditions.jsx

import React from 'react';
import { Box, Typography, Link, Divider } from '@mui/material';
import Header from '../header/Header';
import TermsStyles from './TermsAndConditions.styles';

const toc = [
  "Our Services",
  "Intellectual Property Rights",
  "User Representations",
  "User Registration",
  "Purchases and Payment",
  "Subscriptions",
  "Prohibited Activities",
  "User Generated Contributions",
  "Contribution License",
  "Third-Party Websites and Content",
  "Advertisers",
  "Services Management",
  "Privacy Policy",
  "DMCA Notice and Policy",
  "Term and Termination",
  "Modifications and Interruptions",
  "Governing Law",
  "Dispute Resolution",
  "Corrections",
  "Disclaimer",
  "Limitations of Liability",
  "Indemnification",
  "User Data",
  "Electronic Communications, Transactions, and Signatures",
  "SMS Text Messaging",
  "California Users and Residents",
  "Miscellaneous",
  "Hold Harmless and Waiver of Liability",
  "Warranty Disclaimer / Intellectual Rights",
  "Contact Us"
];

const TermsAndConditions = () => {
  return (
    <>
      <Header />
      <Box sx={TermsStyles.container}>
        <Typography variant="h4" sx={TermsStyles.title}>
          Terms and Conditions
        </Typography>
        <Typography variant="subtitle1" sx={TermsStyles.subtitle}>
          Last Updated: February 19, 2025
        </Typography>

        {/* Table of Contents */}
        <Box sx={TermsStyles.section}>
          <Typography variant="h6" sx={TermsStyles.sectionTitle}>Table of Contents</Typography>
          <ul style={{ marginLeft: 0 }}>
            {toc.map((item, i) => (
              <li key={item}>
                <Link href={`#section-${i + 1}`} underline="hover" color="primary">
                  {i + 1}. {item}
                </Link>
              </li>
            ))}
          </ul>
        </Box>

        {/* Section 1: Our Services */}
        <Divider sx={{ my: 4 }} />
        <Box sx={TermsStyles.section} id="section-1">
          <Typography variant="h6" sx={TermsStyles.sectionTitle}>1. Our Services</Typography>
          <Typography variant="body1" sx={TermsStyles.text}>
            Tenant Guided Tours (operated by Doors, Inc.) provides automated rental showing services and keyless access management to renters, property owners, and managers. Showing service orders are opened as needed (with a one-time fee) and are active until the property is leased. Keyless access management software is used for remote access for tenants, maintenance, management, and owners. Service use is not permitted in any jurisdiction or context where it would violate local law, or for users under 18 years old. We do not tailor the Services for industry-specific regulations (such as HIPAA, FISMA, or GLBA)【14†source】.
          </Typography>
        </Box>

        {/* Section 2: Intellectual Property Rights */}
        <Divider sx={{ my: 4 }} />
        <Box sx={TermsStyles.section} id="section-2">
          <Typography variant="h6" sx={TermsStyles.sectionTitle}>2. Intellectual Property Rights</Typography>
          <Typography variant="body1" sx={TermsStyles.text}>
            Tenant Guided Tours owns or licenses all content, code, software, designs, trademarks, and graphics on our platform ("Content" and "Marks"). Your use is strictly limited to personal, non-commercial purposes. No reproduction, redistribution, or commercial exploitation is permitted without written consent. Any submissions or contributions you make (such as feedback or content posted to forums) grant us broad rights to use, display, and modify that content. We may remove any contributions at our discretion. Copyright issues should be reported per our DMCA section【14†source】.
          </Typography>
        </Box>

        {/* Section 3: User Representations */}
        <Divider sx={{ my: 4 }} />
        <Box sx={TermsStyles.section} id="section-3">
          <Typography variant="h6" sx={TermsStyles.sectionTitle}>3. User Representations</Typography>
          <Typography variant="body1" sx={TermsStyles.text}>
            By using our Services, you warrant that: (a) all registration info you submit is true and accurate; (b) you are at least 18 and have legal capacity; (c) you will not use the Services for unlawful purposes or through automated means; and (d) your use will not violate any law or regulation. We may suspend or terminate accounts with false or incomplete information【14†source】.
          </Typography>
        </Box>

        {/* Section 4: User Registration */}
        <Divider sx={{ my: 4 }} />
        <Box sx={TermsStyles.section} id="section-4">
          <Typography variant="h6" sx={TermsStyles.sectionTitle}>4. User Registration</Typography>
          <Typography variant="body1" sx={TermsStyles.text}>
            You may be required to register and are responsible for maintaining the confidentiality of your password. We reserve the right to remove, reclaim, or change usernames that are inappropriate or otherwise objectionable at our sole discretion【14†source】.
          </Typography>
        </Box>

        {/* Section 5: Purchases and Payment */}
        <Divider sx={{ my: 4 }} />
        <Box sx={TermsStyles.section} id="section-5">
          <Typography variant="h6" sx={TermsStyles.sectionTitle}>5. Purchases and Payment</Typography>
          <Typography variant="body1" sx={TermsStyles.text}>
            We accept Visa, Mastercard, and American Express. You agree to provide accurate purchase and account information and to promptly update your info. All payments are in US dollars. We reserve the right to correct pricing errors and limit or refuse orders at our discretion, including suspected resellers or distributors【14†source】.
          </Typography>
        </Box>

        {/* Section 6: Subscriptions */}
        <Divider sx={{ my: 4 }} />
        <Box sx={TermsStyles.section} id="section-6">
          <Typography variant="h6" sx={TermsStyles.sectionTitle}>6. Subscriptions</Typography>
          <Typography variant="body1" sx={TermsStyles.text}>
            Subscriptions auto-renew monthly unless cancelled. New users receive a 14-day free trial for access management only. You may cancel anytime via our contact info; cancellation takes effect at the end of the paid term. Fees and billing cycles may change with notice【14†source】.
          </Typography>
        </Box>

        {/* Section 7: Prohibited Activities */}
        <Divider sx={{ my: 4 }} />
        <Box sx={TermsStyles.section} id="section-7">
          <Typography variant="h6" sx={TermsStyles.sectionTitle}>7. Prohibited Activities</Typography>
          <Typography variant="body1" sx={TermsStyles.text}>
            Users may not use the Services for unauthorized commercial purposes, data scraping, fraud, illegal activities, harassment, uploading malware, or circumventing security. Other prohibited actions include impersonation, spamming, hacking, reverse engineering, and competitive use. Violation may result in account termination【14†source】.
          </Typography>
        </Box>

        {/* Section 8: User Generated Contributions */}
        <Divider sx={{ my: 4 }} />
        <Box sx={TermsStyles.section} id="section-8">
          <Typography variant="h6" sx={TermsStyles.sectionTitle}>8. User Generated Contributions</Typography>
          <Typography variant="body1" sx={TermsStyles.text}>
            Any content you submit to our site (such as forum posts, comments, or uploads) must be lawful, original, and not infringe any third-party rights. We may remove or modify content at our discretion. You grant us a broad license to use, display, and adapt your contributions【14†source】.
          </Typography>
        </Box>

        {/* Section 9: Contribution License */}
        <Divider sx={{ my: 4 }} />
        <Box sx={TermsStyles.section} id="section-9">
          <Typography variant="h6" sx={TermsStyles.sectionTitle}>9. Contribution License</Typography>
          <Typography variant="body1" sx={TermsStyles.text}>
            By posting content, you grant us a worldwide, royalty-free, perpetual license to use, reproduce, and display your content for any purpose, commercial or otherwise. You retain full ownership but waive moral rights, and we may edit or remove contributions as needed【14†source】.
          </Typography>
        </Box>

        {/* Section 10: Third-Party Websites and Content */}
        <Divider sx={{ my: 4 }} />
        <Box sx={TermsStyles.section} id="section-10">
          <Typography variant="h6" sx={TermsStyles.sectionTitle}>10. Third-Party Websites and Content</Typography>
          <Typography variant="body1" sx={TermsStyles.text}>
            Our Services may link to third-party websites or content, but we do not monitor or endorse them. Access and use of third-party content is at your own risk and governed by their policies. We are not responsible for your interactions or transactions with external sites【14†source】.
          </Typography>
        </Box>

        {/* Section 11: Advertisers */}
        <Divider sx={{ my: 4 }} />
        <Box sx={TermsStyles.section} id="section-11">
          <Typography variant="h6" sx={TermsStyles.sectionTitle}>11. Advertisers</Typography>
          <Typography variant="body1" sx={TermsStyles.text}>
            We may display ads on our Services but do not endorse or have a relationship with advertisers beyond providing ad space【14†source】.
          </Typography>
        </Box>

        {/* Section 12: Services Management */}
        <Divider sx={{ my: 4 }} />
        <Box sx={TermsStyles.section} id="section-12">
          <Typography variant="h6" sx={TermsStyles.sectionTitle}>12. Services Management</Typography>
          <Typography variant="body1" sx={TermsStyles.text}>
            We reserve the right to monitor, remove, restrict, or disable content, files, or access at our sole discretion to protect our platform and comply with law【14†source】.
          </Typography>
        </Box>

        {/* Section 13: Privacy Policy */}
        <Divider sx={{ my: 4 }} />
        <Box sx={TermsStyles.section} id="section-13">
          <Typography variant="h6" sx={TermsStyles.sectionTitle}>13. Privacy Policy</Typography>
          <Typography variant="body1" sx={TermsStyles.text}>
            Data privacy is important to us. Use of our Services is subject to our <Link href="/privacy" color="primary" underline="hover">Privacy Policy</Link>, which is incorporated into these Terms. Services are hosted in the U.S., and by using our platform, you consent to U.S. data processing【14†source】.
          </Typography>
        </Box>

        {/* Section 14: DMCA Notice and Policy */}
        <Divider sx={{ my: 4 }} />
        <Box sx={TermsStyles.section} id="section-14">
          <Typography variant="h6" sx={TermsStyles.sectionTitle}>14. Digital Millennium Copyright Act (DMCA) Notice and Policy</Typography>
          <Typography variant="body1" sx={TermsStyles.text}>
            We respect intellectual property rights. If you believe your copyright is being infringed, please contact our Designated Copyright Agent at info@mytenantaccess.com for takedown requests and counter-notifications, per U.S. law【14†source】.
          </Typography>
        </Box>

        {/* Section 15: Term and Termination */}
        <Divider sx={{ my: 4 }} />
        <Box sx={TermsStyles.section} id="section-15">
          <Typography variant="h6" sx={TermsStyles.sectionTitle}>15. Term and Termination</Typography>
          <Typography variant="body1" sx={TermsStyles.text}>
            We reserve the right to terminate or restrict your use of the Services at any time, without notice, for any breach or for any other reason. You may not register a new account if your previous account is terminated【14†source】.
          </Typography>
        </Box>

        {/* Section 16: Modifications and Interruptions */}
        <Divider sx={{ my: 4 }} />
        <Box sx={TermsStyles.section} id="section-16">
          <Typography variant="h6" sx={TermsStyles.sectionTitle}>16. Modifications and Interruptions</Typography>
          <Typography variant="body1" sx={TermsStyles.text}>
            We may change, suspend, or discontinue any part of our Services at any time without notice. We are not liable for any downtime, interruptions, or loss resulting from such changes or outages【14†source】.
          </Typography>
        </Box>

        {/* Section 17: Governing Law */}
        <Divider sx={{ my: 4 }} />
        <Box sx={TermsStyles.section} id="section-17">
          <Typography variant="h6" sx={TermsStyles.sectionTitle}>17. Governing Law</Typography>
          <Typography variant="body1" sx={TermsStyles.text}>
            These Terms are governed by the laws of the State of Colorado, USA. Any disputes must be resolved under Colorado law【14†source】.
          </Typography>
        </Box>

        {/* Section 18: Dispute Resolution */}
        <Divider sx={{ my: 4 }} />
        <Box sx={TermsStyles.section} id="section-18">
          <Typography variant="h6" sx={TermsStyles.sectionTitle}>18. Dispute Resolution</Typography>
          <Typography variant="body1" sx={TermsStyles.text}>
            Disputes must be resolved first by informal negotiation (minimum 30 days), then binding arbitration under AAA rules in Denver, CO, unless the dispute relates to IP or other excluded matters. No class actions or jury trials. If required, court jurisdiction is also Denver, CO【14†source】.
          </Typography>
        </Box>

        {/* Section 19: Corrections */}
        <Divider sx={{ my: 4 }} />
        <Box sx={TermsStyles.section} id="section-19">
          <Typography variant="h6" sx={TermsStyles.sectionTitle}>19. Corrections</Typography>
          <Typography variant="body1" sx={TermsStyles.text}>
            We may correct any errors, inaccuracies, or omissions on our Services at any time without prior notice【14†source】.
          </Typography>
        </Box>

        {/* Section 20: Disclaimer */}
        <Divider sx={{ my: 4 }} />
        <Box sx={TermsStyles.section} id="section-20">
          <Typography variant="h6" sx={TermsStyles.sectionTitle}>20. Disclaimer</Typography>
          <Typography variant="body1" sx={TermsStyles.text}>
            Services are provided "AS IS" and "AS AVAILABLE." We disclaim all warranties to the fullest extent permitted by law. Use the Services at your own risk【14†source】.
          </Typography>
        </Box>

        {/* Section 21: Limitations of Liability */}
        <Divider sx={{ my: 4 }} />
        <Box sx={TermsStyles.section} id="section-21">
          <Typography variant="h6" sx={TermsStyles.sectionTitle}>21. Limitations of Liability</Typography>
          <Typography variant="body1" sx={TermsStyles.text}>
            Our liability is limited to the amount paid by you in the six months before a claim arises. We are not liable for indirect, consequential, incidental, or special damages, except where prohibited by law【14†source】.
          </Typography>
        </Box>

        {/* Section 22: Indemnification */}
        <Divider sx={{ my: 4 }} />
        <Box sx={TermsStyles.section} id="section-22">
          <Typography variant="h6" sx={TermsStyles.sectionTitle}>22. Indemnification</Typography>
          <Typography variant="body1" sx={TermsStyles.text}>
            You agree to indemnify and hold us harmless from claims, losses, or damages (including legal fees) resulting from your Contributions, use of Services, or violations of these Terms【14†source】.
          </Typography>
        </Box>

        {/* Section 23: User Data */}
        <Divider sx={{ my: 4 }} />
        <Box sx={TermsStyles.section} id="section-23">
          <Typography variant="h6" sx={TermsStyles.sectionTitle}>23. User Data</Typography>
          <Typography variant="body1" sx={TermsStyles.text}>
            We may store data transmitted by you for managing Services, but we are not liable for loss or corruption of such data. You are responsible for your data backups【14†source】.
          </Typography>
        </Box>

        {/* Section 24: Electronic Communications, Transactions, and Signatures */}
        <Divider sx={{ my: 4 }} />
        <Box sx={TermsStyles.section} id="section-24">
          <Typography variant="h6" sx={TermsStyles.sectionTitle}>24. Electronic Communications, Transactions, and Signatures</Typography>
          <Typography variant="body1" sx={TermsStyles.text}>
            You consent to receive communications from us electronically and agree that electronic agreements, signatures, and records are legally binding as originals【14†source】.
          </Typography>
        </Box>

        {/* Section 25: SMS Text Messaging */}
        <Divider sx={{ my: 4 }} />
        <Box sx={TermsStyles.section} id="section-25">
          <Typography variant="h6" sx={TermsStyles.sectionTitle}>25. SMS Text Messaging</Typography>
          <Typography variant="body1" sx={TermsStyles.text}>
            We may send you SMS texts only for showing or system notifications. To opt out, reply "STOP." Standard message/data rates apply. For support, email info@mytenantaccess.com or call 1-800-346-8999【14†source】.
          </Typography>
        </Box>

        {/* Section 26: California Users and Residents */}
        <Divider sx={{ my: 4 }} />
        <Box sx={TermsStyles.section} id="section-26">
          <Typography variant="h6" sx={TermsStyles.sectionTitle}>26. California Users and Residents</Typography>
          <Typography variant="body1" sx={TermsStyles.text}>
            If you have unresolved complaints, you may contact the Complaint Assistance Unit of the California Department of Consumer Affairs (address and phone in full policy)【14†source】.
          </Typography>
        </Box>

        {/* Section 27: Miscellaneous */}
        <Divider sx={{ my: 4 }} />
        <Box sx={TermsStyles.section} id="section-27">
          <Typography variant="h6" sx={TermsStyles.sectionTitle}>27. Miscellaneous</Typography>
          <Typography variant="body1" sx={TermsStyles.text}>
            These Terms (and posted policies) are the entire agreement between you and us. If any part is invalid, it does not affect the rest. We may assign rights, are not liable for delays beyond control, and these Terms cannot be construed against us for drafting【14†source】.
          </Typography>
        </Box>

        {/* Section 28: Hold Harmless and Waiver of Liability */}
        <Divider sx={{ my: 4 }} />
        <Box sx={TermsStyles.section} id="section-28">
          <Typography variant="h6" sx={TermsStyles.sectionTitle}>28. Hold Harmless and Waiver of Liability</Typography>
          <Typography variant="body1" sx={TermsStyles.text}>
            Tenant Guided Tours verifies user IDs to the best of its ability, but all parties (tenants, owners, managers) are responsible for their own due diligence. We are not liable for representations by third parties or for any showing/site hazards or hacking. In case of data breaches, we will act promptly and notify affected users【14†source】.
          </Typography>
        </Box>

        {/* Section 29: Warranty Disclaimer / Intellectual Rights */}
        <Divider sx={{ my: 4 }} />
        <Box sx={TermsStyles.section} id="section-29">
          <Typography variant="h6" sx={TermsStyles.sectionTitle}>29. Warranty Disclaimer / Intellectual Rights</Typography>
          <Typography variant="body1" sx={TermsStyles.text}>
            All Services are provided "as is" without warranty of any kind, express or implied. You are responsible for using compatible devices and a working internet connection. All intellectual property rights remain with Tenant Guided Tours【14†source】.
          </Typography>
        </Box>

        {/* Section 30: Contact Us */}
        <Divider sx={{ my: 4 }} />
        <Box sx={TermsStyles.section} id="section-30">
          <Typography variant="h6" sx={TermsStyles.sectionTitle}>30. Contact Us</Typography>
          <Typography variant="body1" sx={TermsStyles.text}>
            For questions or complaints regarding these Terms or the Services, contact:
            <br /><br />
            <strong>Doors, Inc.</strong><br />
            600 17th Street, Suite 2804V<br />
            Denver, CO 80202, United States<br />
            <strong>Phone:</strong> 1-800-346-8999<br />
            <strong>Email:</strong> <Link href="mailto:info@mytenantaccess.com">info@mytenantaccess.com</Link>
          </Typography>
        </Box>
      </Box>
    </>
  );
};

export default TermsAndConditions;
